// src/components/portal/ProfileManagement.jsx
import React, { useEffect, useState, useMemo } from "react";
import { format } from "date-fns";
import {
  Check,
  CheckCircle,
  Loader2,
  Sparkles,
  UserCircle,
  Wand2,
  User,
  Mail,
  Phone,
  Calendar,
  Award,
  FileText,
  Settings,
  Heart,
  Zap,
  Eye,
  Upload,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import clsx from "clsx";

import { useAuth } from "../auth/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Separator } from "../ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

import {
  fetchTherapistProfile,
  updateTherapistProfile,
  upsertTherapistPersona,
  aiPersonaFromBio,
} from "../../lib/therapistApi";

/* ---------------- helpers ---------------- */
const emptyProfessionalForm = Object.freeze({
  fullName: "",
  email: "",
  phone: "",
  bio: "",
  massageTypes: "",
  experience: "",
  profilePhotoUrl: "",
  clientGenderPreference: "no-preference",
  crmtaCertificateNumber: "",
  crmtaCertificateImage: "",
  crmtaCertificateExpiry: "",
});

const buildFormState = (source, fallbackUser) => ({
  fullName:
    source?.fullName ||
    source?.full_name ||
    source?.name ||
    fallbackUser?.fullName ||
    "",
  email:
    source?.email ||
    source?.emailAddress ||
    source?.email_address ||
    fallbackUser?.email ||
    "",
  phone: source?.phone || source?.phoneNumber || source?.phone_number || "",
  bio: source?.bio || "",
  massageTypes: Array.isArray(source?.massageTypes)
    ? source.massageTypes.join(", ")
    : source?.massageTypes || "",
  experience:
    source?.experience !== undefined && source?.experience !== null
      ? String(source.experience)
      : "",
  profilePhotoUrl: source?.profilePhotoUrl || "",
  clientGenderPreference: source?.clientGenderPreference || "no-preference",
  crmtaCertificateNumber: source?.crmtaCertificateNumber || "",
  crmtaCertificateImage: source?.crmtaCertificateImage || "",
  crmtaCertificateExpiry: source?.crmtaCertificateExpiry
    ? new Date(source.crmtaCertificateExpiry).toISOString().split('T')[0]
    : "",
});

const V = {
  comfortVibe: [
    { v: "DogPlayfulGrounding", label: "Loyal & playful", icon: "🐶" },
    { v: "CatCalmSoothing", label: "Calm & soothing", icon: "🐱" },
    { v: "BirdLightUplifting", label: "Light & uplifting", icon: "🐦" },
    { v: "PlantSteadyGrounding", label: "Steady & grounding", icon: "🌱" },
    { v: "Flexible", label: "Flexible", icon: "🤷" },
  ],
  idealEscape: [
    { v: "Nature", label: "Into nature", icon: "🏞️" },
    { v: "Cozy", label: "A cozy corner", icon: "📚" },
    { v: "Creative", label: "A creative space", icon: "🎶" },
    { v: "Social", label: "Out with friends", icon: "🎉" },
    { v: "Rest", label: "Just resting", icon: "🛌" },
  ],
  resetRitual: [
    { v: "Water", label: "Water", icon: "🌊" },
    { v: "Warmth", label: "Warmth", icon: "🔥" },
    { v: "Air", label: "Fresh air", icon: "🍃" },
    { v: "Sound", label: "Sound", icon: "🎵" },
    { v: "Silence", label: "Silence", icon: "🌌" },
  ],
  therapistEnergy: [
    { v: "Zen", label: "Zen Monk", icon: "🌿" },
    { v: "BestFriend", label: "Best Friend", icon: "🗣️" },
    { v: "Guide", label: "Intuitive Guide", icon: "🔮" },
    { v: "Coach", label: "Motivator Coach", icon: "⚡" },
  ],
  desiredOutcome: [
    { v: "CloudLight", label: "Light as a cloud", icon: "☁️" },
    { v: "Energized", label: "Energized", icon: "🔋" },
    { v: "Balanced", label: "Balanced", icon: "🧘" },
    { v: "CaredFor", label: "Cared for", icon: "❤️" },
  ],
  moodColor: [
    { v: "Blue", label: "Blue", icon: "🔵" },
    { v: "Yellow", label: "Yellow", icon: "🌕" },
    { v: "Red", label: "Red", icon: "🔴" },
    { v: "Green", label: "Green", icon: "🟢" },
    { v: "Black", label: "Black", icon: "⚫" },
  ],
};

function Pill({ active, onClick, icon, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "px-3 sm:px-4 py-2 sm:py-3 rounded-lg border text-xs sm:text-sm flex items-center gap-2 sm:gap-3 transition-all duration-200 font-medium",
        active
          ? "bg-indigo-600 text-white border-indigo-600 shadow-md sm:transform sm:scale-105"
          : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700",
      )}>
      <span className="text-base sm:text-lg leading-none flex-shrink-0">{icon}</span>
      <span className="flex-1 text-left">{children}</span>
      {active && <Check className="h-3 w-3 sm:h-4 sm:w-4 ml-auto flex-shrink-0" />}
    </button>
  );
}

function Section({ title, options, value, onPick }) {
  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="text-sm sm:text-base font-semibold text-slate-900">{title}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
        {options.map((o) => (
          <Pill
            key={o.v}
            active={value === o.v}
            onClick={() => onPick(o.v)}
            icon={o.icon}>
            {o.label}
          </Pill>
        ))}
      </div>
    </div>
  );
}

/* ---------------- main ---------------- */
const ProfileManagement = () => {
  const { user, therapistId, profileComplete, checkProfileCompletion } =
    useAuth();
  const [profile, setProfile] = useState(null);
  const [formState, setFormState] = useState(emptyProfessionalForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);

  // BookingFlow-aligned therapist persona
  const [persona, setPersona] = useState({
    comfortVibe: null,
    idealEscape: null,
    resetRitual: null,
    therapistEnergy: null,
    desiredOutcome: null,
    moodColor: null,
    focusAreas: [],
  });
  const [personaSaving, setPersonaSaving] = useState(false);
  const [personaSavedAt, setPersonaSavedAt] = useState(null);

  // Massage types selection state
  const [massageTypes, setMassageTypes] = useState([]);
  const [isLoadingMassageTypes, setIsLoadingMassageTypes] = useState(false);
  const [massageTypeSearch, setMassageTypeSearch] = useState("");
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [otherMassageInput, setOtherMassageInput] = useState("");

  // Fetch massage types from API
  useEffect(() => {
    const fetchMassageTypes = async () => {
      setIsLoadingMassageTypes(true);
      try {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.bookatherapy.com/api";
        const res = await fetch(`${API_BASE}/pricing/services`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error("Failed to fetch massage services");
        }

        const data = await res.json();
        // Extract service names from the response
        const serviceNames = (data.services || []).map(
          (service) => service.name,
        );
        setMassageTypes(serviceNames);
      } catch (error) {
        console.error("Error fetching massage types:", error);
        toast.error("Failed to load massage types. Using default list.");
        setMassageTypes([]);
      } finally {
        setIsLoadingMassageTypes(false);
      }
    };

    fetchMassageTypes();
  }, []);

  useEffect(() => {
    if (!therapistId) {
      console.warn("No therapist ID available, cannot load profile");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const data = await fetchTherapistProfile(therapistId);
        const t = data?.therapist || data; // handle both shapes
        setProfile(t);
        setFormState(buildFormState(t, user));

        const p = t?.personaClientAxes || {};
        setPersona({
          comfortVibe: p.comfortVibe ?? null,
          idealEscape: p.idealEscape ?? null,
          resetRitual: p.resetRitual ?? null,
          therapistEnergy: p.therapistEnergy ?? null,
          desiredOutcome: p.desiredOutcome ?? null,
          moodColor: p.moodColor ?? null,
          focusAreas: Array.isArray(p.focusAreas) ? p.focusAreas : [],
        });
      } catch (error) {
        toast.error(error?.message || "Failed to load profile");
        setFormState(buildFormState(null, user));
      } finally {
        setLoading(false);
      }
    })();
  }, [therapistId, user]);

  // Show completion modal when profile is incomplete
  useEffect(() => {
    if (!loading && !profileComplete) {
      setShowCompletionModal(true);
    }
  }, [loading, profileComplete]);

  // Get selected massage types as array
  const selectedMassageTypes = useMemo(() => {
    if (!formState.massageTypes) return [];
    if (Array.isArray(formState.massageTypes)) {
      return formState.massageTypes;
    }
    return formState.massageTypes
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }, [formState.massageTypes]);

  // Filter massage types based on search
  const filteredMassageTypes = useMemo(() => {
    if (!massageTypeSearch.trim()) {
      return massageTypes;
    }
    return massageTypes.filter((type) =>
      type.toLowerCase().includes(massageTypeSearch.toLowerCase()),
    );
  }, [massageTypes, massageTypeSearch]);

  const toggleMassageType = (type) => {
    const currentTypes = selectedMassageTypes;
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter((t) => t !== type)
      : [...currentTypes, type];

    updateProfileField("massageTypes", newTypes.join(", "));
  };

  const removeMassageType = (type) => {
    const newTypes = selectedMassageTypes.filter((t) => t !== type);
    updateProfileField("massageTypes", newTypes.join(", "));
  };

  const handleOtherInputChange = (e) => {
    const value = e.target.value;
    setOtherMassageInput(value);

    // Check if user added a comma
    if (value.endsWith(",")) {
      const newType = value.slice(0, -1).trim();
      if (newType && !selectedMassageTypes.includes(newType)) {
        const newTypes = [...selectedMassageTypes, newType];
        updateProfileField("massageTypes", newTypes.join(", "));
      }
      setOtherMassageInput("");
    }
  };

  const updateProfileField = (key, value) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
    setProfile((prev) => {
      if (!prev) return prev;
      if (key === "massageTypes") {
        return {
          ...prev,
          massageTypes: value
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        };
      }
      if (key === "experience") {
        return { ...prev, experience: Number(value) || 0 };
      }
      return { ...prev, [key]: value };
    });
  };

  const handleProfileSave = async () => {
    if (!therapistId) return;
    try {
      setSaving(true);
      const payload = {
        fullName: formState.fullName?.trim() || "",
        email: formState.email?.trim() || "",
        phone: formState.phone?.trim() || "",
        bio: formState.bio?.trim() || undefined,
        massageTypes: formState.massageTypes
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        experience: Number(formState.experience) || 0,
        clientGenderPreference: formState.clientGenderPreference || "no-preference",
        crmtaCertificateNumber: formState.crmtaCertificateNumber?.trim() || "",
        crmtaCertificateExpiry: formState.crmtaCertificateExpiry || "",
      };

      // Only include photo if it exists (don't send empty string)
      if (formState.profilePhotoUrl) {
        payload.profilePhotoUrl = formState.profilePhotoUrl;
      }

      // Only include certificate image if it exists
      if (formState.crmtaCertificateImage) {
        payload.crmtaCertificateImage = formState.crmtaCertificateImage;
      }

      const res = await updateTherapistProfile(therapistId, payload);
      const updated = res?.therapist || res;
      setProfile(updated);
      setFormState(buildFormState(updated, user));
      toast.success("Profile updated");

      // Re-check profile completion after save
      if (checkProfileCompletion) {
        await checkProfileCompletion(therapistId);
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- persona actions ---------------- */
  const pick = (key, value) =>
    setPersona((prev) => ({
      ...prev,
      [key]: prev[key] === value ? null : value,
    }));

  const handlePersonaSave = async () => {
    try {
      setPersonaSaving(true);
      const required = [
        "comfortVibe",
        "idealEscape",
        "resetRitual",
        "therapistEnergy",
        "desiredOutcome",
        "moodColor",
      ];
      const missing = required.filter((k) => !persona[k]);
      if (missing.length) {
        toast.error(`Please choose: ${missing.join(", ")}`);
        return;
      }
      const payload = {
        comfortVibe: persona.comfortVibe,
        idealEscape: persona.idealEscape,
        resetRitual: persona.resetRitual,
        therapistEnergy: persona.therapistEnergy,
        desiredOutcome: persona.desiredOutcome,
        moodColor: persona.moodColor,
        focusAreas: persona.focusAreas || [],
      };
      const r = await upsertTherapistPersona(therapistId, payload);
      const updated = r?.therapist || {};
      setProfile((prev) => ({
        ...prev,
        personaClientAxes: updated.personaClientAxes || payload,
      }));
      setPersonaSavedAt(new Date());
      toast.success("Persona saved. Matching updated!");

      // Re-check profile completion after persona save
      if (checkProfileCompletion) {
        await checkProfileCompletion(therapistId);
      }
    } catch (e) {
      toast.error(e?.message || "Failed to save persona");
    } finally {
      setPersonaSaving(false);
    }
  };

  const handleAutofillFromBio = async () => {
    try {
      setPersonaSaving(true);
      if (!formState.bio || !formState.bio.trim()) {
        toast.error("Add a short bio first, then try Autofill.");
        return;
      }
      console.log("Generating AI persona from bio for therapist:", therapistId);
      console.log("Bio text:", formState.bio.trim());
      const draft = await aiPersonaFromBio(therapistId, formState.bio.trim());
      console.log("AI persona response:", draft);

      if (draft?.debug) {
        console.error("AI Persona Debug Info:", draft.debug);
        console.error("Raw OpenAI response:", draft.debug.rawContent);
        console.error("Parsed persona:", draft.debug.parsedPersona);
        console.error("Validation errors:", draft.debug.validationErrors);
      }

      if (draft?.aiPersona) {
        setPersona((prev) => ({ ...prev, ...draft.aiPersona }));
        toast.success("Draft generated from bio. Review & Save.");
      } else {
        toast.error(draft?.message || "Could not generate persona from bio. Please check console for details.");
      }
    } catch (e) {
      console.error("AI persona generation error:", e);
      const errorMsg = e?.response?.data?.message || e?.message || "Failed to generate persona. Please try again.";
      toast.error(errorMsg);
    } finally {
      setPersonaSaving(false);
    }
  };

  const handleFocusAreasChange = (e) => {
    const arr = (e.target.value || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    setPersona((prev) => ({ ...prev, focusAreas: arr }));
  };

  /* ---------------- render ---------------- */
  if (!therapistId) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-sm text-gray-600">
        Therapist ID missing. Log in again to continue.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const getInitials = (name) => {
    if (!name) return "T";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <>
      {/* Profile Completion Modal */}
      <Dialog open={showCompletionModal} onOpenChange={setShowCompletionModal}>
        <DialogContent className="sm:max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl sm:text-2xl">
              <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-indigo-600" />
              Complete Your Profile
            </DialogTitle>
            <DialogDescription className="text-sm sm:text-base pt-4">
              Complete your profile to get bookings from Book a Therapy.{" "}
              <span className="font-semibold text-slate-700">
                It takes less than 2 mins to complete the profile!
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 pt-4">
            <div className="bg-indigo-50 rounded-lg p-3 sm:p-4 border border-indigo-200">
              <h4 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                <CheckCircle className="h-4 w-4" />
                What you need to complete:
              </h4>
              <ul className="text-xs sm:text-sm text-indigo-800 space-y-1 ml-5 sm:ml-6 list-disc">
                <li>Profile photo</li>
                <li>Basic contact information</li>
                <li>Professional bio and massage types</li>
                <li>Therapeutic persona preferences</li>
              </ul>
            </div>
            <Button
              onClick={() => setShowCompletionModal(false)}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white text-sm sm:text-base">
              Got it, let's complete my profile!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
          {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-2">
                Therapist Profile
              </h1>
              <p className="text-slate-600 text-sm sm:text-base lg:text-lg">
                Manage your professional information and therapeutic persona
              </p>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="text-right flex-1 sm:flex-none">
                <div className="text-xs sm:text-sm text-slate-500">Profile Status</div>
                <div className="text-xl sm:text-2xl font-bold text-slate-900">
                  {profile ? "Complete" : "Incomplete"}
                </div>
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Profile Overview Card */}
        <Card className="bg-white border-0 shadow-lg mb-6 sm:mb-8">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
              <Avatar className="w-16 h-16 sm:w-20 sm:h-20 mx-auto sm:mx-0">
                {formState.profilePhotoUrl ? (
                  <img
                    src={formState.profilePhotoUrl}
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xl sm:text-2xl font-bold">
                    {getInitials(formState.fullName)}
                  </AvatarFallback>
                )}
              </Avatar>
              <div className="flex-1 w-full">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2 text-center sm:text-left">
                  {formState.fullName || "Your Name"}
                </h2>
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-slate-600 mb-3">
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm sm:text-base break-all">{formState.email || "your@email.com"}</span>
                  </div>
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <Phone className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm sm:text-base">{formState.phone || "Your phone"}</span>
                  </div>
                  <div className="flex items-center gap-2 justify-center sm:justify-start">
                    <Calendar className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm sm:text-base">{formState.experience || 0} years experience</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                  {(formState.massageTypes || "").split(",").map(
                    (massageType, index) =>
                      massageType.trim() && (
                        <Badge
                          key={index}
                          variant="outline"
                          className="bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">
                          {massageType.trim()}
                        </Badge>
                      ),
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-6 sm:gap-8 lg:grid-cols-[1fr,400px]">
          {/* Main Profile Form */}
          <div className="space-y-6 sm:space-y-8">
            {/* Professional Information */}
            <Card className="bg-white border-0 shadow-lg">
              <CardHeader className="pb-4 px-4 sm:px-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Settings className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg sm:text-xl font-semibold text-slate-900">
                      Professional Information
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-slate-600 mt-1">
                      Keep your contact details and credentials up to date
                    </p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
                {/* Profile Photo Upload */}
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6">
                    <Avatar className="w-20 h-20 sm:w-24 sm:h-24 border-4 border-white shadow-lg flex-shrink-0">
                      {formState.profilePhotoUrl ? (
                        <img
                          src={formState.profilePhotoUrl}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <AvatarFallback className="bg-indigo-100 text-indigo-600 text-2xl sm:text-3xl font-bold">
                          {getInitials(formState.fullName)}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <div className="flex-1 w-full text-center sm:text-left">
                      <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-2 flex items-center justify-center sm:justify-start gap-2">
                        <UserCircle className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
                        Profile Photo
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">
                          Required
                        </Badge>
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600 mb-3">
                        Upload a professional profile photo. This will be visible to clients.
                      </p>
                      <label className="cursor-pointer inline-block w-full sm:w-auto">
                        <div className="flex items-center justify-center gap-2 px-4 py-2 bg-white border-2 border-indigo-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors w-full sm:w-auto">
                          <Upload className="h-4 w-4 text-indigo-600" />
                          <span className="text-xs sm:text-sm font-medium text-indigo-700">
                            {formState.profilePhotoUrl
                              ? "Change Photo"
                              : "Upload Photo"}
                          </span>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                updateProfileField("profilePhotoUrl", reader.result);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                      Full Name
                    </label>
                    <Input
                      value={formState.fullName}
                      onChange={(e) =>
                        updateProfileField("fullName", e.target.value)
                      }
                      placeholder="Enter your full name"
                      className="bg-slate-50 border-slate-200 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      value={formState.email}
                      onChange={(e) =>
                        updateProfileField("email", e.target.value)
                      }
                      placeholder="your@email.com"
                      className="bg-slate-50 border-slate-200 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                      Phone Number
                    </label>
                    <Input
                      value={formState.phone}
                      onChange={(e) =>
                        updateProfileField("phone", e.target.value)
                      }
                      placeholder="+1 (555) 123-4567"
                      className="bg-slate-50 border-slate-200 text-sm sm:text-base"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                      Years of Experience
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={formState.experience}
                      onChange={(e) =>
                        updateProfileField("experience", e.target.value)
                      }
                      placeholder="5"
                      className="bg-slate-50 border-slate-200 text-sm sm:text-base"
                    />
                  </div>
                </div>

                <Separator className="my-4" />

                <div>
                  <h3 className="text-sm sm:text-base font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Award className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600" />
                    CRMTA Certification
                  </h3>
                  <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                        Certificate Number
                      </label>
                      <Input
                        value={formState.crmtaCertificateNumber}
                        onChange={(e) =>
                          updateProfileField("crmtaCertificateNumber", e.target.value)
                        }
                        placeholder="Enter CRMTA certificate number"
                        className="bg-slate-50 border-slate-200 text-sm sm:text-base"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                        Certificate Expiry Date
                      </label>
                      <Input
                        type="date"
                        value={formState.crmtaCertificateExpiry}
                        onChange={(e) =>
                          updateProfileField("crmtaCertificateExpiry", e.target.value)
                        }
                        className="bg-slate-50 border-slate-200 text-sm sm:text-base"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                      Certificate Image
                    </label>
                    <div className="flex items-center gap-4">
                      <label className="flex-1 cursor-pointer">
                        <div className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-3 border-2 border-dashed border-slate-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors">
                          <Upload className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                          <span className="text-xs sm:text-sm text-slate-600 text-center">
                            {formState.crmtaCertificateImage
                              ? "Change certificate image"
                              : "Upload certificate image"}
                          </span>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                updateProfileField("crmtaCertificateImage", reader.result);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </label>
                    </div>
                    {formState.crmtaCertificateImage && (
                      <div className="mt-3">
                        <img
                          src={formState.crmtaCertificateImage}
                          alt="CRMTA Certificate"
                          className="max-w-full sm:max-w-xs rounded-lg border border-slate-200 shadow-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <Separator className="my-4" />

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                    Massage Types *
                  </label>
                  <div className="space-y-3">
                    {/* Selected massage types badges */}
                    {selectedMassageTypes.length > 0 && (
                      <div className="flex flex-wrap gap-2 p-2 sm:p-3 border border-slate-200 rounded-md bg-slate-50">
                        {selectedMassageTypes.map((type) => (
                          <Badge
                            key={type}
                            variant="outline"
                            className="flex items-center gap-1 bg-indigo-50 text-indigo-700 border-indigo-200 text-xs">
                            <span>{type}</span>
                            <X
                              className="h-3 w-3 cursor-pointer hover:text-red-600"
                              onClick={() => removeMassageType(type)}
                            />
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Search bar for massage types */}
                    {!isLoadingMassageTypes && massageTypes.length > 0 && (
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                          placeholder="Search massage types..."
                          value={massageTypeSearch}
                          onChange={(e) => setMassageTypeSearch(e.target.value)}
                          className="pl-9 bg-slate-50 border-slate-200 text-sm sm:text-base"
                        />
                      </div>
                    )}

                    {/* Massage types grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[200px] overflow-y-auto border border-slate-200 rounded-md p-2 sm:p-3 bg-white">
                      {isLoadingMassageTypes ? (
                        <div className="col-span-2 flex items-center justify-center p-4">
                          <Loader2 className="h-4 w-4 animate-spin mr-2 text-indigo-600" />
                          <span className="text-sm text-slate-600">
                            Loading massage types...
                          </span>
                        </div>
                      ) : filteredMassageTypes.length > 0 ? (
                        filteredMassageTypes.map((type) => (
                          <div
                            key={type}
                            onClick={() => toggleMassageType(type)}
                            className={clsx(
                              "flex items-center justify-between p-2.5 border rounded-md cursor-pointer transition-all",
                              selectedMassageTypes.includes(type)
                                ? "bg-indigo-50 border-indigo-500"
                                : "hover:bg-slate-50 border-slate-200",
                            )}>
                            <span className="text-sm text-slate-700">{type}</span>
                            {selectedMassageTypes.includes(type) && (
                              <Check className="h-4 w-4 text-indigo-600" />
                            )}
                          </div>
                        ))
                      ) : massageTypes.length > 0 && massageTypeSearch.trim() ? (
                        <div className="col-span-2 text-sm text-slate-500 p-2">
                          No massage types found matching "{massageTypeSearch}"
                        </div>
                      ) : massageTypes.length === 0 ? (
                        <div className="col-span-2 text-sm text-slate-500 p-2">
                          No massage types available. Please add services in the pricing section.
                        </div>
                      ) : null}

                      {/* Other option */}
                      <div
                        onClick={() => {
                          setShowOtherInput(!showOtherInput);
                        }}
                        className={clsx(
                          "flex items-center justify-between p-2.5 border rounded-md cursor-pointer transition-all",
                          showOtherInput
                            ? "bg-indigo-50 border-indigo-500"
                            : "hover:bg-slate-50 border-slate-200",
                        )}>
                        <span className="text-sm font-medium text-slate-700">
                          Other
                        </span>
                        {showOtherInput && (
                          <Check className="h-4 w-4 text-indigo-600" />
                        )}
                      </div>
                    </div>

                    {/* Other input field */}
                    {showOtherInput && (
                      <div>
                        <label className="block text-xs text-slate-600 mb-1">
                          Type custom massage types and press comma to add
                        </label>
                        <Textarea
                          value={otherMassageInput}
                          onChange={handleOtherInputChange}
                          placeholder="Type a massage type and add a comma to create a badge..."
                          rows={2}
                          className="bg-slate-50 border-slate-200 resize-none"
                        />
                      </div>
                    )}

                    {selectedMassageTypes.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        At least one massage type is required
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">
                    Professional Bio
                  </label>
                  <Textarea
                    rows={5}
                    value={formState.bio}
                    onChange={(e) => updateProfileField("bio", e.target.value)}
                    placeholder="Describe your therapeutic approach, philosophy, and what makes your practice unique..."
                    className="bg-slate-50 border-slate-200 resize-none text-sm sm:text-base"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Help clients understand your approach and expertise
                  </p>
                </div>

                <Separator className="my-4" />

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-3">
                    Client Gender Preference
                  </label>
                  <p className="text-xs text-slate-500 mb-4">
                    Specify if you have a preference for working with specific client genders. Select "No Preference" to accept all clients.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
                    {[
                      { value: "no-preference", label: "No Preference", icon: "👥", description: "Accept all clients" },
                      { value: "female", label: "Female Clients Only", icon: "👩", description: "Only female clients" },
                      { value: "male", label: "Male Clients Only", icon: "👨", description: "Only male clients" },
                      { value: "both", label: "Both Genders", icon: "🤝", description: "Male and female clients" },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => updateProfileField("clientGenderPreference", option.value)}
                        className={clsx(
                          "flex flex-col items-center gap-1 sm:gap-2 p-3 sm:p-4 rounded-lg border-2 transition-all duration-200",
                          formState.clientGenderPreference === option.value
                            ? "bg-indigo-50 border-indigo-600 text-indigo-700 shadow-md"
                            : "bg-white border-slate-200 text-slate-700 hover:border-indigo-300 hover:bg-indigo-50"
                        )}
                      >
                        <span className="text-xl sm:text-2xl">{option.icon}</span>
                        <span className="text-xs sm:text-sm font-medium text-center">{option.label}</span>
                        <span className="text-xs text-slate-500 text-center hidden sm:block">{option.description}</span>
                        {formState.clientGenderPreference === option.value && (
                          <Check className="h-3 w-3 sm:h-4 sm:w-4 text-indigo-600 mt-1" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-slate-200">
                  <Button
                    onClick={handleProfileSave}
                    disabled={saving}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 sm:px-8 gap-2 w-full sm:w-auto">
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    <Settings className="h-4 w-4" />
                    <span className="text-sm sm:text-base">Save Profile</span>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Therapeutic Persona */}
            <Card className="bg-white border-0 shadow-lg">
              <CardHeader className="pb-4 px-4 sm:px-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Heart className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg sm:text-xl font-semibold text-slate-900">
                      Therapeutic Persona
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-slate-600 mt-1">
                      Define your therapeutic style for better client matching
                    </p>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-6 sm:space-y-8 px-4 sm:px-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
                  <div className="flex items-start gap-2 sm:gap-3">
                    <Zap className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-blue-900 mb-1">
                        How This Works
                      </h4>
                      <p className="text-xs sm:text-sm text-blue-700">
                        Choose one option in each section below. These
                        preferences are matched directly against your clients'
                        answers to create better therapeutic connections.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-6 sm:space-y-8">
                  <Section
                    title="Comfort Vibe"
                    options={V.comfortVibe}
                    value={persona.comfortVibe}
                    onPick={(v) => pick("comfortVibe", v)}
                  />
                  <Separator />
                  <Section
                    title="Ideal Escape"
                    options={V.idealEscape}
                    value={persona.idealEscape}
                    onPick={(v) => pick("idealEscape", v)}
                  />
                  <Separator />
                  <Section
                    title="Reset Ritual"
                    options={V.resetRitual}
                    value={persona.resetRitual}
                    onPick={(v) => pick("resetRitual", v)}
                  />
                  <Separator />
                  <Section
                    title="Therapist Energy"
                    options={V.therapistEnergy}
                    value={persona.therapistEnergy}
                    onPick={(v) => pick("therapistEnergy", v)}
                  />
                  <Separator />
                  <Section
                    title="Desired Outcome"
                    options={V.desiredOutcome}
                    value={persona.desiredOutcome}
                    onPick={(v) => pick("desiredOutcome", v)}
                  />
                  <Separator />
                  <Section
                    title="Mood Color"
                    options={V.moodColor}
                    value={persona.moodColor}
                    onPick={(v) => pick("moodColor", v)}
                  />
                  <Separator />

                  <div className="space-y-3 sm:space-y-4">
                    <div className="text-sm sm:text-base font-semibold text-slate-900">
                      Focus Areas (Optional)
                    </div>
                    <Input
                      placeholder="e.g., Neck, Shoulders, Lower back, Anxiety, Trauma"
                      value={(persona.focusAreas || []).join(", ")}
                      onChange={handleFocusAreasChange}
                      className="bg-slate-50 border-slate-200 text-sm sm:text-base"
                    />
                    <p className="text-xs sm:text-sm text-slate-500">
                      Specify areas of focus to help refine client matches
                    </p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 pt-4 border-t border-slate-200">
                  <Button
                    onClick={handlePersonaSave}
                    disabled={personaSaving}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 sm:px-6 gap-2 w-full sm:w-auto">
                    {personaSaving && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                    <Heart className="h-4 w-4" />
                    <span className="text-sm sm:text-base">Save Persona</span>
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleAutofillFromBio}
                    disabled={personaSaving}
                    className="border-slate-200 hover:border-purple-300 gap-2 w-full sm:w-auto">
                    <Wand2 className="h-4 w-4" />
                    <span className="text-sm sm:text-base">AI Generate from Bio</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 sm:space-y-6">
            {/* Persona Preview */}
            <Card className="bg-white border-0 shadow-lg">
              <CardHeader className="pb-3 px-4 sm:px-6">
                <CardTitle className="text-sm sm:text-base font-semibold text-slate-900 flex items-center gap-2">
                  <Eye className="h-4 w-4 text-slate-600" />
                  Persona Preview
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 sm:px-6">
                <div className="space-y-3 sm:space-y-4">
                  {persona.comfortVibe && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs sm:text-sm text-slate-600">
                        Comfort Vibe
                      </span>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {
                          V.comfortVibe.find((v) => v.v === persona.comfortVibe)
                            ?.label
                        }
                      </Badge>
                    </div>
                  )}
                  {persona.idealEscape && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs sm:text-sm text-slate-600">
                        Ideal Escape
                      </span>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {
                          V.idealEscape.find((v) => v.v === persona.idealEscape)
                            ?.label
                        }
                      </Badge>
                    </div>
                  )}
                  {persona.therapistEnergy && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs sm:text-sm text-slate-600">Energy</span>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {
                          V.therapistEnergy.find(
                            (v) => v.v === persona.therapistEnergy,
                          )?.label
                        }
                      </Badge>
                    </div>
                  )}
                  {persona.desiredOutcome && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs sm:text-sm text-slate-600">Outcome</span>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {
                          V.desiredOutcome.find(
                            (v) => v.v === persona.desiredOutcome,
                          )?.label
                        }
                      </Badge>
                    </div>
                  )}
                  {persona.moodColor && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs sm:text-sm text-slate-600">Mood Color</span>
                      <Badge variant="outline" className="text-xs flex-shrink-0">
                        {
                          V.moodColor.find((v) => v.v === persona.moodColor)
                            ?.label
                        }
                      </Badge>
                    </div>
                  )}
                  {persona.focusAreas && persona.focusAreas.length > 0 && (
                    <div>
                      <span className="text-xs sm:text-sm text-slate-600 block mb-2">
                        Focus Areas
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {persona.focusAreas.map((area, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            className="text-xs">
                            {area}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <Separator className="my-4" />

                <div className="text-xs text-slate-500">
                  {personaSavedAt ? (
                    <div className="flex items-center gap-2">
                      <Check className="h-3 w-3 text-green-600" />
                      Saved {format(personaSavedAt, "MMM d, h:mm a")}
                    </div>
                  ) : (
                    "Not saved yet"
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Profile Stats */}
            <Card className="bg-white border-0 shadow-lg">
              <CardHeader className="pb-3 px-4 sm:px-6">
                <CardTitle className="text-sm sm:text-base font-semibold text-slate-900 flex items-center gap-2">
                  <Award className="h-4 w-4 text-slate-600" />
                  Profile Completion
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 px-4 sm:px-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs sm:text-sm text-slate-600">Profile Photo</span>
                    <Badge
                      variant="outline"
                      className={
                        formState.profilePhotoUrl
                          ? "bg-green-50 text-green-700 border-green-200 text-xs flex-shrink-0"
                          : "bg-amber-50 text-amber-700 border-amber-200 text-xs flex-shrink-0"
                      }>
                      {formState.profilePhotoUrl
                        ? "Complete"
                        : "Incomplete"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs sm:text-sm text-slate-600">Basic Info</span>
                    <Badge
                      variant="outline"
                      className={
                        formState.fullName && formState.email
                          ? "bg-green-50 text-green-700 border-green-200 text-xs flex-shrink-0"
                          : "bg-amber-50 text-amber-700 border-amber-200 text-xs flex-shrink-0"
                      }>
                      {formState.fullName && formState.email
                        ? "Complete"
                        : "Incomplete"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs sm:text-sm text-slate-600">
                      Bio & Massage Types
                    </span>
                    <Badge
                      variant="outline"
                      className={
                        formState.bio && formState.massageTypes
                          ? "bg-green-50 text-green-700 border-green-200 text-xs flex-shrink-0"
                          : "bg-amber-50 text-amber-700 border-amber-200 text-xs flex-shrink-0"
                      }>
                      {formState.bio && formState.massageTypes
                        ? "Complete"
                        : "Incomplete"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs sm:text-sm text-slate-600">
                      Therapeutic Persona
                    </span>
                    <Badge
                      variant="outline"
                      className={
                        persona.comfortVibe && persona.therapistEnergy
                          ? "bg-green-50 text-green-700 border-green-200 text-xs flex-shrink-0"
                          : "bg-amber-50 text-amber-700 border-amber-200 text-xs flex-shrink-0"
                      }>
                      {persona.comfortVibe && persona.therapistEnergy
                        ? "Complete"
                        : "Incomplete"}
                    </Badge>
                  </div>
                </div>

                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-600 mb-1">
                    Overall Progress
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${
                          [
                            formState.profilePhotoUrl,
                            formState.fullName && formState.email,
                            formState.bio && formState.massageTypes,
                            persona.comfortVibe && persona.therapistEnergy,
                          ].filter(Boolean).length * 25
                        }%`,
                      }}></div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {
                      [
                        formState.profilePhotoUrl,
                        formState.fullName && formState.email,
                        formState.bio && formState.massageTypes,
                        persona.comfortVibe && persona.therapistEnergy,
                      ].filter(Boolean).length
                    }{" "}
                    of 4 sections complete
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ProfileManagement;
